<template>
    <div>
        <!-- Product list -->
        <q-table
            @request="onRequest"
            :filter="table.search"
            :rows="products"
            :columns="table.cols"
            v-model:pagination="table.pagination"
            :rows-per-page-options="[table.pagination.rowsPerPage]"
            :class="
                $q.screen.gt.lg
                    ? 'sticky-table products-table'
                    : 'products-table__cardmode'
            "
            row-key="id"
            binary-state-sort
            :no-data-label="$t('No data available')"
            :grid="$q.screen.lt.lg"
        >
            <!-- top bar -->
            <template v-slot:top>
                <div
                    :class="$q.screen.gt.md ? 'q-pa-md' : ''"
                    class="full-width"
                >
                    <!-- title -->
                    <div class="col-12">
                        <div class="text-h4 text-weight-medium">
                            {{ $t('Products') }}
                        </div>
                        <div class="page-description">
                            {{
                                $t('Manage products for {facilityName}.', {
                                    facilityName: facility?.name
                                })
                            }}
                        </div>
                    </div>
                    <!-- search bar -->
                    <div
                        class="row fit"
                        :class="
                            $q.screen.lt.sm
                                ? ''
                                : 'wrap justify-between items-start content-start'
                        "
                    >
                        <q-input
                            v-model="table.search"
                            @clear="removeFilteredProductFromStore"
                            color="accent"
                            :placeholder="$t('Search product')"
                            :class="
                                $q.screen.lt.sm
                                    ? 'col-12 q-mb-md'
                                    : 'col q-mr-md'
                            "
                            class="bg-white"
                            debounce="750"
                            clearable
                            outlined
                            dense
                            @input="setFilteredProductInStore"
                        >
                            <template v-slot:prepend>
                                <q-icon name="search" />
                            </template>
                        </q-input>
                        <q-btn
                            @click="
                                $router.push({
                                    name: 'product_create',
                                    params: {
                                        facilityId: facility.id
                                    }
                                })
                            "
                            :disable="$userCannot('delete', 'products')"
                            :label="$t('Add product')"
                            class="table-header__button"
                            :class="$q.screen.lt.sm ? 'col-12' : ''"
                            color="accent"
                            size="md"
                        ></q-btn>
                    </div>
                </div>
            </template>

            <!-- actions column -->
            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <div class="row q-gutter-md">
                        <q-btn
                            @click="
                                $router.push({
                                    name: 'product_update',
                                    params: {
                                        facilityId: facilityId,
                                        productId: props.row.id
                                    },
                                    state: {
                                        name: props.row.name,
                                        code: props.row.code,
                                        description: props.row.description,
                                        unitPacking: props.row.unit_packing,
                                        unitMeasure: props.row.unit_measure,
                                        productCategory:
                                            props.row.product_category,
                                        productProperties: JSON.stringify(
                                            props.row.properties
                                        )
                                    }
                                })
                            "
                            class="table__action-button"
                            color="accent"
                            icon="fas fa-edit"
                            outline
                        />
                        <div>
                            <q-btn
                                @click="remove(props.row)"
                                :disable="$userCannot('delete', 'products')"
                                :color="
                                    $userCannot('delete', 'products')
                                        ? 'grey'
                                        : 'negative'
                                "
                                class="table__action-button"
                                icon="fas fa-trash-alt"
                                outline
                            />
                            <q-tooltip v-if="$userCannot('delete', 'products')">
                                {{
                                    $t(
                                        'You do not have permission to perform this action.'
                                    )
                                }}
                            </q-tooltip>
                        </div>
                    </div>
                </q-td>
            </template>

            <!-- Card template -->
            <template v-slot:item="props">
                <product-card
                    @edit="
                        $router.push({
                            name: 'product_update',
                            params: {
                                facilityId: facilityId,
                                productId: props.row.id
                            },
                            state: {
                                name: props.row.name,
                                code: props.row.code,
                                description: props.row.description,
                                unitPacking: props.row.unit_packing,
                                unitMeasure: props.row.unit_measure,
                                productCategory: props.row.product_category,
                                productProperties: JSON.stringify(
                                    props.row.properties
                                )
                            }
                        })
                    "
                    @delete="remove(props.row)"
                    :name="props.row.name"
                    :description="props.row.description"
                    :unit-packing="props.row.unit_packing"
                    :unit-measure="props.row.unit_measure"
                    :code="props.row.code"
                    :can-edit="$userCan('edit', 'products')"
                    :can-delete="$userCan('delete', 'products')"
                    :product-id="props.row.id"
                    :facility-id="facilityId"
                >
                </product-card>
            </template>
        </q-table>
    </div>
</template>

<script>
import ProductCard from '@components/ProductCard.vue';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import { convertToUnitSystem } from '@models/products.js';
import { roundTo } from '@utils/numberFormatters.js';
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useFiltersStore } from '@stores/filters.js';
import { useFacilitiesStore } from '@stores/facilities.ts';

export default {
    name: 'ProductList',
    components: { ProductCard },
    mixins: [BreadcrumbsManager, UpdateURL],
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    async created() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Settings'),
                route: {
                    name: 'facility_settings',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Products')
            }
        ]);

        // get the store filtered product
        const storedFilteredProduct = this.getStoredFacilityFilter({
            facilityId: this.facilityId,
            filter: 'products'
        });

        // sync the component's search value with the stored
        // filtered product string.
        this.table.search = storedFilteredProduct;
        // get the products from the database.
        this.onRequest({
            pagination: this.table.pagination,
            filter: ''
        });
    },
    data() {
        return {
            baseURL: 'products',
            products: [],

            // table configs
            table: {
                cols: [
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'unit_packing',
                        name: 'unit_packing',
                        label: this.$t('Unit packing'),
                        format: (value, row) => {
                            const { packing } = this.convertedUnits(
                                value,
                                row.unit_measure
                            );
                            return roundTo(packing, 3);
                        }
                    },
                    {
                        align: 'left',
                        field: 'unit_measure',
                        name: 'unit_measure',
                        label: this.$t('Unit measure'),
                        format: (value, row) => {
                            const { measure } = this.convertedUnits(
                                row.unit_packing,
                                value
                            );
                            return measure;
                        }
                    },
                    {
                        align: 'left',
                        field: 'code',
                        name: 'code',
                        label: this.$t('SKU'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'actions',
                        name: 'actions',
                        label: this.$t('Actions')
                    }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                search: ''
            }
        };
    },
    computed: {
        ...mapState(useFiltersStore, ['getStoredFacilityFilter']),
        ...mapState(useFacilitiesStore, ['getFacility']),
        /**
         * Get selected facility.
         * @return {Object} - facility.
         */
        facility() {
            return this.getFacility(this.facilityId);
        },
        /**
         * Get the URL for the API request.
         * @return {String} - API request URL
         */
        url() {
            // get current pagination state attributes
            const { descending, page, sortBy } = this.table.pagination;

            // update URL
            return this.updateURL({
                baseURL: this.baseURL,
                params: {
                    search: this.table.search,
                    ordering: descending ? `-${sortBy}` : `${sortBy}`,
                    page: page,
                    facility: this.facilityId
                }
            });
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useFiltersStore, ['setFacilityFilter']),
        /**
         * Convert unit packing and measure to facility unit system.
         * @param {string} unitPacking - Unit packing value.
         * @param {string} unitMeasure - Unit measure label.
         * @return {Object} - Converted unit packing and measure.
         */
        convertedUnits(unitPacking, unitMeasure) {
            const { value: packing, unit: measure } = convertToUnitSystem(
                parseFloat(unitPacking),
                unitMeasure,
                this.facility?.unit_system
            );
            return { packing, measure };
        },
        /**
         * Fetch products from the server.
         */
        async getProducts() {
            await this.$axios
                .get(this.url)
                .then(response => {
                    // clear existing data
                    this.products = [];
                    // push data
                    this.products.push(...response.data.results);
                    // update total items count
                    this.table.pagination.rowsNumber = response.data.count;
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to load products. Please try again later.'
                        ),
                        error
                    );
                })
                .finally(() => {
                    this.stopLoading();
                });
        },
        /**
         * Delete product.
         * @param {Object} row - Product from table row.
         */
        async remove(row) {
            this.$confirmDelete(
                this.$t('Are you sure you want to delete this product?')
            ).onOk(async () => {
                // begin loading
                this.beginLoading(`${this.$t('Removing product')}...`);

                await this.$axios
                    .delete(`${this.baseURL}/${row.id}/`)
                    .then(response => {
                        // remove item from product list
                        let productIndex = this.products.findIndex(
                            product => product.id === row.id
                        );
                        this.products.splice(productIndex, 1);

                        // notify user
                        this.$notifySuccess(this.$t('Product deleted'));
                    })
                    .catch(error => {
                        this.$notifyError(
                            this.$t(
                                'Failed to remove product. Please try again later.'
                            ),
                            error
                        );
                    })
                    .finally(() => {
                        this.stopLoading();
                    });
            });
        },
        /**
         * Fetch data from the server. Triggered by any table sort/search/page changes.
         * @param {Object} props
         * @param {Object} props.pagination
         * @param {string} props.filter
         */
        async onRequest(props) {
            // update pagination object
            this.table.pagination = props.pagination;

            // begin loading
            this.beginLoading(`${this.$t('Loading products')}...`);

            // fetch data from the server
            await this.getProducts();
        },

        /**
         * Update the filtered product for particular facility,
         * in the store.
         * Depends on the value of table.search
         */
        setFilteredProductInStore() {
            this.setFacilityFilter({
                facilityId: this.facilityId,
                filter: 'products',
                value: this.table.search
            });
        },

        /**
         * Remove the filtered product of particular facility from the store.
         * And reset the search key of this.table
         * to its initial value.
         */
        removeFilteredProductFromStore() {
            this.setFacilityFilter({
                facilityId: this.facilityId,
                filter: 'products',
                value: ''
            });
            this.table.search = '';
        }
    }
};
</script>

<style lang="scss" scoped>
.products-table {
    height: 80vh;
}

// align table top with cards
.products-table__cardmode :deep(.q-table__top) {
    padding: 0px;
}
</style>
